import React, { useState, useEffect } from "react";
import {
    Box,
    TextField,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    IconButton,
    Collapse,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TableContainer,
    Paper,
    Snackbar,
    Alert,
    Chip,
    Avatar,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import HistoryIcon from "@mui/icons-material/History";
import SyncIcon from "@mui/icons-material/Sync";
import axios from "axios";
import { API_URL as API_BASE } from "../config";
import { useAuth } from "../auth/AuthContext";

const API_URL = `${API_BASE}/customers`;
const WIX_API = `${API_BASE}/integrations/wix`;

const Customers = () => {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [customers, setCustomers] = useState([]);
    const [filteredCustomers, setFilteredCustomers] = useState([]);
    const [contacts, setContacts] = useState({});
    const [tasks, setTasks] = useState({}); // Add tasks state
    const [customerForm, setCustomerForm] = useState({
        name: "",
        phone: "",
        email: "",
        address: { street: "", city: "", state: "", zip: "" },
        sector: "",
        notes: "",
        scheduled_visits: "",
        website: "",
    });
    const [contactForm, setContactForm] = useState({
        name: "",
        phone: "",
        email: "",
        notes: "",
    });
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isContactModalOpen, setIsContactModalOpen] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [currentCustomer, setCurrentCustomer] = useState(null);
    const [currentCustomerId, setCurrentCustomerId] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    const [searchValue, setSearchValue] = useState("");
    const [expandedCustomer, setExpandedCustomer] = useState(null);
    const [isAddNoteModalOpen, setIsAddNoteModalOpen] = useState(false);
    const [currentCustomerForNote, setCurrentCustomerForNote] = useState(null);
    const [newNote, setNewNote] = useState("");
    const [historyOpen, setHistoryOpen] = useState(false);
    const [historyCustomer, setHistoryCustomer] = useState(null);
    const [revisions, setRevisions] = useState([]);
    const [historyBusy, setHistoryBusy] = useState(false);
    const [wixSyncing, setWixSyncing] = useState(false);

    const parseAddress = (value) => {
        const text = String(value || "").trim();
        if (!text) return { street: "", city: "", state: "", zip: "" };
        const parts = text.split(",").map((part) => part.trim()).filter(Boolean);
        if (parts.length < 3) return { street: text, city: "", state: "", zip: "" };
        const street = parts[0];
        const city = parts[1];
        const stateZip = parts[2].match(/^(.+?)\s+(\d{5}(?:-\d{4})?)$/);
        return {
            street,
            city,
            state: stateZip ? stateZip[1] : parts[2],
            zip: stateZip ? stateZip[2] : "",
        };
    };
    useEffect(() => {
        fetchCustomers();
        fetchAllTasks();
    }, []);

    const fetchCustomers = async () => {
        try {
            const response = await axios.get(API_URL);
            const customerRows = Array.isArray(response.data)
                ? response.data
                : Array.isArray(response.data?.customers)
                    ? response.data.customers
                    : [];
            setCustomers(customerRows);
            setFilteredCustomers(customerRows);
        } catch (error) {
            setSnackbar({ open: true, message: "Failed to fetch customers.", severity: "error" });
        }
    };

    const fetchContacts = async (customerId) => {
        try {
            const response = await axios.get(`${API_URL}/${customerId}/contacts`);
            const contactRows = Array.isArray(response.data)
                ? response.data
                : Array.isArray(response.data?.contacts)
                    ? response.data.contacts
                    : [];
            setContacts((prev) => ({ ...prev, [customerId]: contactRows }));
        } catch (error) {
            setSnackbar({ open: true, message: "Failed to fetch contacts.", severity: "error" });
        }
    };

    const handleSearch = (e) => {
        const value = e.target.value.toLowerCase();
        setSearchValue(value);
        setFilteredCustomers(
            customers.filter(
                (customer) =>
                    customer.name.toLowerCase().includes(value) ||
                    (customer.sector && customer.sector.toLowerCase().includes(value))
            )
        );
    };
    const toggleCollapse = (id) => {
        setExpandedCustomer((prev) => (prev === id ? null : id));
        if (!contacts[id]) fetchContacts(id);
    };
    const openAddNoteModal = (customer) => {
        setCurrentCustomerForNote(customer);
        setIsAddNoteModalOpen(true);
        setNewNote("");
    };
    const handleAddNote = async () => {
        if (!newNote.trim()) {
            setSnackbar({ open: true, message: "Note cannot be empty.", severity: "warning" });
            return;
        }

        try {
            const updatedNotes = `${currentCustomerForNote.notes || ""}\n${newNote}`;
            const updatedCustomer = { ...currentCustomerForNote, notes: updatedNotes };

            await axios.put(`${API_URL}/${currentCustomerForNote.id}`, updatedCustomer);

            setSnackbar({ open: true, message: "Note added successfully!", severity: "success" });
            fetchCustomers(); // Refresh customers
            setIsAddNoteModalOpen(false);
        } catch (error) {
            setSnackbar({ open: true, message: "Failed to add note.", severity: "error" });
        }
    };

    const handleSaveCustomer = async () => {
        const addressString = `${customerForm.address.street}, ${customerForm.address.city}, ${customerForm.address.state} ${customerForm.address.zip}`;
        const payload = { ...customerForm, address: addressString };

        try {
            if (isEdit) {
                await axios.put(`${API_URL}/${currentCustomer.id}`, payload);
                setSnackbar({ open: true, message: "Customer updated successfully!", severity: "success" });
            } else {
                await axios.post(API_URL, payload);
                setSnackbar({ open: true, message: "Customer added successfully!", severity: "success" });
            }
            fetchCustomers();
            setIsModalOpen(false);
        } catch (error) {
            setSnackbar({ open: true, message: "Failed to save customer.", severity: "error" });
        }
    };

    const handleDeleteCustomer = async (id) => {
        if (!window.confirm("Delete this customer and their contacts? Linked quotes/events stay but are unlinked.")) {
            return;
        }
        try {
            await axios.delete(`${API_URL}/${id}`);
            setSnackbar({ open: true, message: "Customer deleted successfully!", severity: "success" });
            fetchCustomers();
        } catch (error) {
            setSnackbar({
                open: true,
                message: error.response?.data?.error || "Failed to delete customer.",
                severity: "error",
            });
        }
    };

    const handleOpenCustomerModal = (customer = null) => {
        setIsEdit(!!customer);
        setCurrentCustomer(customer);
        if (customer) {
            setCustomerForm({
                ...customer,
                phone: customer.phone || "",
                email: customer.email || "",
                address: parseAddress(customer.address),
            });
        } else {
            setCustomerForm({
                name: "",
                phone: "",
                email: "",
                address: { street: "", city: "", state: "", zip: "" },
                sector: "",
                notes: "",
                scheduled_visits: "",
                website: "",
            });
        }
        setIsModalOpen(true);
    };
    const handleOpenContactModal = (contact = null, customerId) => {
        setIsContactModalOpen(true);
        setCurrentCustomerId(customerId);
        if (contact) {
            setContactForm(contact);
        } else {
            setContactForm({
                id: null,
                name: "",
                phone: "",
                email: "",
                notes: "",
            });
        }
    };

    const handleSaveContact = async () => {
        const payload = { ...contactForm, customer_id: currentCustomerId };

        try {
            if (contactForm.id) {
                await axios.put(`${API_URL}/${currentCustomerId}/contacts/${contactForm.id}`, payload);
                setSnackbar({ open: true, message: "Contact updated successfully!", severity: "success" });
            } else {
                await axios.post(`${API_URL}/${currentCustomerId}/contacts`, payload);
                setSnackbar({ open: true, message: "Contact added successfully!", severity: "success" });
            }
            fetchContacts(currentCustomerId);
            setIsContactModalOpen(false);
        } catch (error) {
            setSnackbar({ open: true, message: "Failed to save contact.", severity: "error" });
        }
    };

    const handleDeleteContact = async (customerId, contactId) => {
        try {
            await axios.delete(`${API_URL}/${customerId}/contacts/${contactId}`);
            setSnackbar({ open: true, message: "Contact deleted successfully!", severity: "success" });
            fetchContacts(customerId);
        } catch (error) {
            setSnackbar({ open: true, message: "Failed to delete contact.", severity: "error" });
        }
    };
    const fetchAllTasks = async () => {
        try {
            const response = await axios.get(`${API_BASE}/tasks`);
            const allTasks = Array.isArray(response.data)
                ? response.data
                : Array.isArray(response.data?.tasks)
                    ? response.data.tasks
                    : [];

            // Group tasks by customerId
            const groupedTasks = allTasks.reduce((acc, task) => {
                if (!acc[task.customerId]) acc[task.customerId] = [];
                acc[task.customerId].push(task);
                return acc;
            }, {});

            setTasks(groupedTasks);
        } catch (error) {
            console.error("Error fetching tasks:", error);
        }
    };

    const isWixCustomer = (customer) =>
        customer?.wix_source === "wix" ||
        (customer?.keywords && String(customer.keywords).toLowerCase().includes("wix"));

    async function openHistory(customer) {
        setHistoryCustomer(customer);
        setHistoryOpen(true);
        setHistoryBusy(true);
        setRevisions([]);
        try {
            const { data } = await axios.get(`${API_URL}/${customer.id}/revisions`, {
                withCredentials: true,
            });
            setRevisions(data.items || []);
        } catch (error) {
            setSnackbar({
                open: true,
                message: error.response?.data?.error || "Could not load history.",
                severity: "error",
            });
        } finally {
            setHistoryBusy(false);
        }
    }

    async function restoreRevision(revisionId) {
        if (!historyCustomer?.id) return;
        if (!window.confirm("Restore this customer to the selected snapshot?")) return;
        setHistoryBusy(true);
        try {
            await axios.post(
                `${API_URL}/${historyCustomer.id}/revisions/${revisionId}/restore`,
                {},
                { withCredentials: true }
            );
            setSnackbar({ open: true, message: "Customer restored from history.", severity: "success" });
            await fetchCustomers();
            await openHistory(historyCustomer);
        } catch (error) {
            setSnackbar({
                open: true,
                message: error.response?.data?.error || "Restore failed.",
                severity: "error",
            });
            setHistoryBusy(false);
        }
    }

    async function handleLiveSiteSync() {
        if (
            !window.confirm(
                "Pull contacts from the live pointblankrobotics.com Wix site into Customers?\n\nA DB backup is taken first. Existing fields are not blanked out by empty Wix values."
            )
        ) {
            return;
        }
        setWixSyncing(true);
        try {
            const res = await axios.post(`${WIX_API}/sync`, {}, { withCredentials: true });
            const r = res.data || {};
            setSnackbar({
                open: true,
                message: `Live site sync: ${r.fetched || 0} contacts · ${r.createdCustomers || 0} new · ${r.updatedCustomers || 0} updated${r.backup ? ` · backup ${r.backup}` : ""}`,
                severity: "success",
            });
            await fetchCustomers();
        } catch (error) {
            setSnackbar({
                open: true,
                message: error.response?.data?.error || error.message || "Live site sync failed.",
                severity: "error",
            });
        } finally {
            setWixSyncing(false);
        }
    }

    return (
        <Box sx={{ p: { xs: 2, md: 3 } }}>
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
            </Snackbar>

            <Box
                display="flex"
                justifyContent="space-between"
                alignItems={{ xs: "stretch", sm: "flex-end" }}
                mb={2.5}
                gap={2}
                flexWrap="wrap"
            >
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: "-0.02em" }}>
                        Customers
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Accounts, contacts, and live Wix sync
                    </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                    <TextField
                        label="Search customers"
                        value={searchValue}
                        onChange={handleSearch}
                        variant="outlined"
                        size="small"
                        sx={{ minWidth: { xs: "100%", sm: 240 } }}
                    />
                    {isAdmin ? (
                        <Button
                            variant="outlined"
                            color="secondary"
                            startIcon={<SyncIcon />}
                            onClick={handleLiveSiteSync}
                            disabled={wixSyncing}
                        >
                            {wixSyncing ? "Syncing…" : "Sync live site"}
                        </Button>
                    ) : null}
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenCustomerModal()}
                    >
                        Add Customer
                    </Button>
                </Box>
            </Box>

            <Paper
                elevation={0}
                sx={{
                    overflow: "hidden",
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 2,
                    bgcolor: "background.paper",
                }}
            >
                <Box
                    sx={{
                        display: { xs: "none", md: "grid" },
                        gridTemplateColumns: "minmax(240px, 1.5fr) minmax(180px, 1fr) minmax(160px, 1fr) 140px",
                        gap: 2,
                        px: 2.5,
                        py: 1.25,
                        borderBottom: 1,
                        borderColor: "divider",
                        bgcolor: "action.hover",
                        color: "text.secondary",
                        fontSize: "0.7rem",
                        fontWeight: 800,
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                    }}
                >
                    <Box>Account</Box>
                    <Box>Contact</Box>
                    <Box>Plan / sector</Box>
                    <Box textAlign="right">Actions</Box>
                </Box>

                {filteredCustomers.length === 0 ? (
                    <Box px={3} py={6} textAlign="center">
                        <Typography color="text.secondary">No customers match your search.</Typography>
                    </Box>
                ) : null}

                {filteredCustomers.map((customer) => {
                    const open = expandedCustomer === customer.id;
                    const customerTasks = tasks[customer.id] || [];
                    const customerContacts = contacts[customer.id] || [];
                    return (
                        <Box
                            key={customer.id}
                            sx={{
                                borderBottom: 1,
                                borderColor: "divider",
                                "&:last-child": { borderBottom: 0 },
                            }}
                        >
                            <Box
                                onClick={() => toggleCollapse(customer.id)}
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: {
                                        xs: "1fr auto",
                                        md: "minmax(240px, 1.5fr) minmax(180px, 1fr) minmax(160px, 1fr) 140px",
                                    },
                                    gap: 2,
                                    alignItems: "center",
                                    px: { xs: 1.5, md: 2.5 },
                                    py: 1.5,
                                    cursor: "pointer",
                                    transition: "background-color 120ms ease",
                                    bgcolor: open ? "action.selected" : "transparent",
                                    "&:hover": { bgcolor: "action.hover" },
                                }}
                            >
                                <Box display="flex" alignItems="center" minWidth={0} gap={1.25}>
                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            toggleCollapse(customer.id);
                                        }}
                                        sx={{ color: "primary.main" }}
                                    >
                                        {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    </IconButton>
                                    <Avatar
                                        sx={{
                                            width: 36,
                                            height: 36,
                                            bgcolor: "primary.main",
                                            color: "primary.contrastText",
                                            fontSize: "0.85rem",
                                            fontWeight: 800,
                                        }}
                                    >
                                        {String(customer.name || "?").trim().slice(0, 1).toUpperCase()}
                                    </Avatar>
                                    <Box minWidth={0}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }} noWrap>
                                            {customer.name}
                                        </Typography>
                                        <Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap" mt={0.5}>
                                            {isWixCustomer(customer) ? (
                                                <Chip label="Wix" size="small" color="secondary" variant="outlined" />
                                            ) : null}
                                            {customer.pricing_plan_name ? (
                                                <Chip
                                                    label={customer.pricing_plan_name}
                                                    size="small"
                                                    color={Number(customer.pricing_plan_active) ? "success" : "warning"}
                                                    variant="outlined"
                                                    sx={{ maxWidth: 200 }}
                                                />
                                            ) : null}
                                        </Box>
                                    </Box>
                                </Box>

                                <Box sx={{ display: { xs: "none", md: "block" }, minWidth: 0 }}>
                                    {customer.primary_contact_name &&
                                        customer.primary_contact_name.toLowerCase() !==
                                        String(customer.name || "").toLowerCase() ? (
                                        <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                                            {customer.primary_contact_name}
                                        </Typography>
                                    ) : null}
                                    <Typography variant="body2" noWrap>
                                        {customer.email || "No email"}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" noWrap display="block">
                                        {customer.phone || "No phone"}
                                    </Typography>
                                </Box>

                                <Box sx={{ display: { xs: "none", md: "block" }, minWidth: 0 }}>
                                    <Typography variant="body2" noWrap>
                                        {customer.pricing_plan_name || "No plan"}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" noWrap display="block">
                                        {customer.sector || "Uncategorized"}
                                    </Typography>
                                </Box>

                                <Box
                                    textAlign="right"
                                    onClick={(e) => e.stopPropagation()}
                                    sx={{ whiteSpace: "nowrap" }}
                                >
                                    <IconButton
                                        size="small"
                                        title="Add note"
                                        onClick={() => openAddNoteModal(customer)}
                                        sx={{ color: "text.secondary" }}
                                    >
                                        <NoteAddIcon fontSize="small" />
                                    </IconButton>
                                    {isAdmin ? (
                                        <IconButton
                                            size="small"
                                            title="History"
                                            onClick={() => openHistory(customer)}
                                            sx={{ color: "text.secondary" }}
                                        >
                                            <HistoryIcon fontSize="small" />
                                        </IconButton>
                                    ) : null}
                                    <IconButton
                                        size="small"
                                        title="Edit"
                                        onClick={() => handleOpenCustomerModal(customer)}
                                        sx={{ color: "text.secondary" }}
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        color="error"
                                        title="Delete"
                                        onClick={() => handleDeleteCustomer(customer.id)}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            </Box>

                            <Collapse in={open} timeout="auto" unmountOnExit>
                                <Box
                                    sx={{
                                        px: { xs: 2, md: 3 },
                                        py: 2.5,
                                        borderTop: 1,
                                        borderColor: "divider",
                                        bgcolor: "background.default",
                                    }}
                                >
                                    <Box
                                        display="grid"
                                        gridTemplateColumns={{ xs: "1fr", md: "1.2fr 1fr 1fr" }}
                                        gap={3}
                                        mb={3}
                                    >
                                        <Box>
                                            <Typography
                                                variant="overline"
                                                color="text.secondary"
                                                sx={{ letterSpacing: "0.1em", fontWeight: 800 }}
                                            >
                                                Details
                                            </Typography>
                                            <Typography variant="body2" sx={{ mt: 0.75 }}>
                                                <Box component="span" color="text.secondary">Sector · </Box>
                                                {customer.sector || "N/A"}
                                            </Typography>
                                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                                                <Box component="span" color="text.secondary">Address · </Box>
                                                {customer.address || "N/A"}
                                            </Typography>
                                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                                                <Box component="span" color="text.secondary">Website · </Box>
                                                {customer.website || "N/A"}
                                            </Typography>
                                            <Typography variant="body2" sx={{ mt: 0.5, display: { md: "none" } }}>
                                                <Box component="span" color="text.secondary">Email · </Box>
                                                {customer.email || "N/A"}
                                            </Typography>
                                            <Typography variant="body2" sx={{ mt: 0.5, display: { md: "none" } }}>
                                                <Box component="span" color="text.secondary">Phone · </Box>
                                                {customer.phone || "N/A"}
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Typography
                                                variant="overline"
                                                color="text.secondary"
                                                sx={{ letterSpacing: "0.1em", fontWeight: 800 }}
                                            >
                                                Notes
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, whiteSpace: "pre-wrap" }}>
                                                {customer.notes ? String(customer.notes).trim() : "No notes yet."}
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Typography
                                                variant="overline"
                                                color="text.secondary"
                                                sx={{ letterSpacing: "0.1em", fontWeight: 800 }}
                                            >
                                                Activity
                                            </Typography>
                                            <Typography variant="body2" sx={{ mt: 0.75 }}>
                                                <Box component="span" color="text.secondary">Tasks · </Box>
                                                {customerTasks.length
                                                    ? customerTasks.map((t) => t.title).join(", ")
                                                    : "None assigned"}
                                            </Typography>
                                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                                                <Box component="span" color="text.secondary">Visits · </Box>
                                                {customer.scheduled_visits || "None scheduled"}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        gap={1}
                                        mb={1.5}
                                        flexWrap="wrap"
                                    >
                                        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                                            Contacts
                                        </Typography>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<AddIcon />}
                                            onClick={() => handleOpenContactModal(null, customer.id)}
                                        >
                                            Add contact
                                        </Button>
                                    </Box>
                                    <TableContainer
                                        sx={{
                                            border: 1,
                                            borderColor: "divider",
                                            borderRadius: 1.5,
                                            bgcolor: "background.paper",
                                        }}
                                    >
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Name</TableCell>
                                                    <TableCell>Phone</TableCell>
                                                    <TableCell>Email</TableCell>
                                                    <TableCell align="right">Actions</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {customerContacts.length === 0 ? (
                                                    <TableRow>
                                                        <TableCell colSpan={4}>
                                                            <Typography variant="body2" color="text.secondary">
                                                                No contacts yet.
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                ) : (
                                                    customerContacts.map((contact) => (
                                                        <TableRow key={contact.id} hover>
                                                            <TableCell>{contact.name}</TableCell>
                                                            <TableCell>{contact.phone || "—"}</TableCell>
                                                            <TableCell>{contact.email || "—"}</TableCell>
                                                            <TableCell align="right">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() =>
                                                                        handleOpenContactModal(contact, customer.id)
                                                                    }
                                                                    sx={{ color: "text.secondary" }}
                                                                >
                                                                    <EditIcon fontSize="small" />
                                                                </IconButton>
                                                                <IconButton
                                                                    size="small"
                                                                    color="error"
                                                                    onClick={() =>
                                                                        handleDeleteContact(customer.id, contact.id)
                                                                    }
                                                                >
                                                                    <DeleteIcon fontSize="small" />
                                                                </IconButton>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            </Collapse>
                        </Box>
                    );
                })}
            </Paper>

            <Dialog open={isModalOpen} onClose={() => setIsModalOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>{isEdit ? "Edit Customer" : "Add Customer"}</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Name"
                        value={customerForm.name}
                        onChange={(e) => setCustomerForm({ ...customerForm, name: e.target.value })}
                        fullWidth
                        margin="normal"
                        required
                    />
                    <Grid container spacing={2}>
                        <Grid size={6}>
                            <TextField
                                label="Phone"
                                value={customerForm.phone}
                                onChange={(e) => setCustomerForm({ ...customerForm, phone: e.target.value })}
                                fullWidth
                                margin="normal"
                            />
                        </Grid>
                        <Grid size={6}>
                            <TextField
                                label="Email"
                                type="email"
                                value={customerForm.email}
                                onChange={(e) => setCustomerForm({ ...customerForm, email: e.target.value })}
                                fullWidth
                                margin="normal"
                            />
                        </Grid>
                        <Grid size={6}>
                            <TextField
                                label="Street"
                                value={customerForm.address.street}
                                onChange={(e) =>
                                    setCustomerForm({
                                        ...customerForm,
                                        address: { ...customerForm.address, street: e.target.value },
                                    })
                                }
                                fullWidth
                                margin="normal"
                            />
                        </Grid>
                        <Grid size={6}>
                            <TextField
                                label="City"
                                value={customerForm.address.city}
                                onChange={(e) =>
                                    setCustomerForm({
                                        ...customerForm,
                                        address: { ...customerForm.address, city: e.target.value },
                                    })
                                }
                                fullWidth
                                margin="normal"
                            />
                        </Grid>
                        <Grid size={6}>
                            <TextField
                                label="State"
                                value={customerForm.address.state}
                                onChange={(e) =>
                                    setCustomerForm({
                                        ...customerForm,
                                        address: { ...customerForm.address, state: e.target.value },
                                    })
                                }
                                fullWidth
                                margin="normal"
                            />
                        </Grid>
                        <Grid size={6}>
                            <TextField
                                label="ZIP"
                                value={customerForm.address.zip}
                                onChange={(e) =>
                                    setCustomerForm({
                                        ...customerForm,
                                        address: { ...customerForm.address, zip: e.target.value },
                                    })
                                }
                                fullWidth
                                margin="normal"
                            />
                        </Grid>
                    </Grid>
                    <TextField
                        label="Scheduled Visits"
                        value={customerForm.scheduled_visits}
                        onChange={(e) => setCustomerForm({ ...customerForm, scheduled_visits: e.target.value })}
                        fullWidth
                        margin="normal"
                    />
                    <TextField
                        label="Website URL"
                        value={customerForm.website}
                        onChange={(e) => setCustomerForm({ ...customerForm, website: e.target.value })}
                        fullWidth
                        margin="normal"
                    />
                    <TextField
                        label="Notes"
                        value={customerForm.notes}
                        onChange={(e) => setCustomerForm({ ...customerForm, notes: e.target.value })}
                        fullWidth
                        margin="normal"
                        multiline
                        rows={3}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsModalOpen(false)}>Cancel</Button>
                    <Button variant="contained" color="primary" onClick={handleSaveCustomer}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={isContactModalOpen} onClose={() => setIsContactModalOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{contactForm.id ? "Edit Contact" : "Add Contact"}</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Name"
                        value={contactForm.name}
                        onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                        fullWidth
                        margin="normal"
                        required
                    />
                    <TextField
                        label="Phone"
                        value={contactForm.phone}
                        onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                        fullWidth
                        margin="normal"
                    />
                    <TextField
                        label="Email"
                        value={contactForm.email}
                        onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                        fullWidth
                        margin="normal"
                    />
                    <TextField
                        label="Notes"
                        value={contactForm.notes}
                        onChange={(e) => setContactForm({ ...contactForm, notes: e.target.value })}
                        fullWidth
                        margin="normal"
                        multiline
                        rows={3}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsContactModalOpen(false)}>Cancel</Button>
                    <Button variant="contained" color="primary" onClick={handleSaveContact}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={isAddNoteModalOpen} onClose={() => setIsAddNoteModalOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Add Note</DialogTitle>
                <DialogContent>
                    <TextField
                        label="New Note"
                        value={newNote}
                        onChange={(e) => setNewNote(e.target.value)}
                        fullWidth
                        multiline
                        rows={4}
                        margin="normal"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsAddNoteModalOpen(false)}>Cancel</Button>
                    <Button variant="contained" color="primary" onClick={handleAddNote}>
                        Add Note
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>
                    History{historyCustomer?.name ? ` — ${historyCustomer.name}` : ""}
                </DialogTitle>
                <DialogContent>
                    {historyBusy && revisions.length === 0 ? (
                        <Typography color="text.secondary">Loading…</Typography>
                    ) : revisions.length === 0 ? (
                        <Typography color="text.secondary">
                            No prior versions yet. Snapshots are saved automatically when a customer is updated or
                            deleted (going forward).
                        </Typography>
                    ) : (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>When</TableCell>
                                    <TableCell>Action</TableCell>
                                    <TableCell>Snapshot</TableCell>
                                    <TableCell align="right">Restore</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {revisions.map((rev) => {
                                    const snap = rev.snapshot || {};
                                    return (
                                        <TableRow key={rev.id}>
                                            <TableCell>
                                                {rev.createdAt ? new Date(rev.createdAt).toLocaleString() : "—"}
                                            </TableCell>
                                            <TableCell>{rev.action}</TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {snap.name || "—"}
                                                    {snap.email ? ` · ${snap.email}` : ""}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    {snap.website || "no website"}
                                                    {snap.phone ? ` · ${snap.phone}` : ""}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Button
                                                    size="small"
                                                    disabled={historyBusy}
                                                    onClick={() => restoreRevision(rev.id)}
                                                >
                                                    Restore
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setHistoryOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Customers;
